import { normalizeSearchText, matchesPatientSearch } from '../../utils/patientSearch';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FileText, Calendar, Users, Search, Download, Trash2, UserPlus, Users as UsersIcon, CreditCard as Edit, Newspaper, PlusCircle, GraduationCap, BookOpen, Stethoscope, MessageSquare, BarChart3, Link as LinkIcon, ShieldAlert, Home } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import DocumentUpload from '../../components/admin/DocumentUpload';
import CreateUserPage from './CreateUserPage';
import EditUserModal from '../../components/admin/EditUserModal';
import NewsEditor from './NewsEditor';
import EventEditor from './EventEditor';
import LeadsManager from './LeadsManager';
import CoursesEditor from './CoursesEditor';
import HomePageEditor from './HomePageEditor';
import { useAuth } from '../../context/AuthContext';
import { getMedicalRecords, deleteMedicalRecord } from '../../utils/dataUtils';
import { toast } from 'react-toastify';
import { User, UserService } from '../../services/UserService';
import { Timestamp } from 'firebase/firestore';
import { DollarSign, Calendar as CalendarIcon, Settings } from 'lucide-react';
import TestimonialsEditor from './TestimonialsEditor';
import AgendaPage from './AgendaPage';
import AgendaReports from './AgendaReports';
import ProntuarioPage from './ProntuarioPage';
import { ClipboardList } from 'lucide-react';
import NewAssessmentsV2Page from './NewAssessmentsV2Page';
import NewFinancialDashboard from './NewFinancialDashboard';
import FinancialReadDashboard from './FinancialReadDashboard';
import FinancialCashFlowPage from './FinancialCashFlowPage';
import { isFinancialPreviewEnabled } from '../../services/FinancialReadService';
import PatientRegistrationPage from './PatientRegistrationPage';
import MigrateMedicalRecordsPage from './MigrateMedicalRecordsPage';
import MonthlyClosingPage from './MonthlyClosingPage';
import PatientLinkingPage from './PatientLinkingPage';
import { NotificationBell } from '../../components/common/NotificationBell';
import { AppointmentStatusModal } from '../../components/common/AppointmentStatusModal';
import { PendingAppointment } from '../../services/AppointmentStatusService';
import DataIntegrityPage from './DataIntegrityPage';
import DataQualityDashboard from './DataQualityDashboard';
import ClinicSettingsPage from './ClinicSettingsPage';
import ActionButton from '../../components/ui/ActionButton';
import DoctorPricingRulesPage from './DoctorPricingRulesPage';
import AboutPageEditor from './AboutPageEditor';
import ServicesPageEditor from './ServicesPageEditor';
import FinancialMigrationControlPage from './FinancialMigrationControlPage';
import FinancialAccountsPayablePage from './FinancialAccountsPayablePage';
import { buildTenantPath, extractTenantSlugFromPathname } from '../../utils/tenantSlug';
import { getMedicalRecordDownloadUrl } from '../../utils/medicalRecordFiles';
interface MedicalRecord {
  id: string;
  name: string;
  url: string;
  uploadDate: Date;
  patientName: string;
  doctorName: string;
  patientId: string;
  doctorId: string;
  metadata: any;
}

const AdminDashboard = () => {
  const { user, selectedClient } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [activeSection, setActiveSection] = useState<'dashboard' | 'content' | 'patients' | 'financial' | 'business' | 'system'>('dashboard');
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [records, setRecords] = useState<MedicalRecord[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [showDeleteUserConfirm, setShowDeleteUserConfirm] = useState<User | null>(null);
  const [showUsersList, setShowUsersList] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showEventEditor, setShowEventEditor] = useState(false);
  const [showAgendaReports, setShowAgendaReports] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<PendingAppointment | null>(null);
  const isSuperAdmin = user?.userGlobalRole === 'superAdmin';
  const activeRole = selectedClient?.userRole || user?.userRole || null;
  const isInternalUser = activeRole === 'interno';
  const isFinanceUser = activeRole === 'financeiro';
  const financialPreview = isFinancialPreviewEnabled(selectedClient?.clientId ?? '');
  const restrictedSystemTabs = new Set(['migrate-records', 'data-integrity', 'data-quality']);
  const tenantSlug = selectedClient?.clientSlug || extractTenantSlugFromPathname(location.pathname);

  // Estrutura das seções e abas
  const sections = {
    dashboard: {
      label: 'Dashboard',
      icon: BarChart3,
      tabs: [
        { id: 'overview', label: 'Visão Geral', icon: BarChart3 }
      ]
    },
    content: {
      label: 'Conteúdo',
      icon: Newspaper,
      tabs: [
        { id: 'home-page', label: 'Página Inicial', icon: Home },
        { id: 'about-page', label: 'Página Sobre', icon: BookOpen },
        { id: 'services-page', label: 'Página de Serviços', icon: Stethoscope },
        { id: 'news', label: 'Notícias', icon: Newspaper },
        { id: 'courses', label: 'Cursos', icon: GraduationCap },
        { id: 'events', label: 'Eventos', icon: Calendar },
        { id: 'testimonials', label: 'Depoimentos', icon: MessageSquare }
      ]
    },
    patients: {
      label: 'Pacientes',
      icon: Stethoscope,
      tabs: [
        { id: 'patient-registration', label: 'Cadastro de Pacientes', icon: UserPlus },
        { id: 'prontuario', label: 'Evoluções Clínicas', icon: FileText },
        { id: 'documents', label: 'Documentos', icon: FileText },
        { id: 'agenda', label: 'Agenda', icon: CalendarIcon },
        { id: 'agenda-reports', label: 'Relatórios', icon: BarChart3 },
        { id: 'assessments-v2', label: 'Avaliações Clínicas', icon: ClipboardList },

      ]
    },
    financial: {
      label: 'Financeiro',
      icon: DollarSign,
      tabs: [
        { id: 'financial-overview', label: 'Visão Geral', icon: BarChart3 },
        ...(financialPreview ? [
          { id: 'financial-organizations', label: 'Organizações e Convênios', icon: FileText },
        ] : []),
        ...(financialPreview ? [
          { id: 'financial-payer-links', label: 'Pagadores', icon: Users },
          { id: 'financial-contracts', label: 'Contratos', icon: FileText },
        ] : []),
        ...(financialPreview ? [{ id: 'financial-packages', label: 'Pacotes e Extratos', icon: FileText }] : []),
        { id: 'financial-invoices', label: 'Faturas', icon: FileText },
        { id: 'financial-professionals', label: 'Repasse', icon: DollarSign },
        ...(financialPreview ? [{ id: 'financial-accounts-payable', label: 'Contas a Pagar', icon: DollarSign }] : []),
        ...(financialPreview ? [{ id: 'financial-cash-flow', label: 'Fluxo de Caixa', icon: DollarSign }] : []),
        ...(financialPreview ? [{ id: 'financial-settings', label: 'Configurações', icon: Settings }] : [])
      ]
    },
    business: {
      label: 'Negócios',
      icon: DollarSign,
      tabs: [
        { id: 'leads', label: 'Leads', icon: Users },
        { id: 'monthly-closing', label: 'Fechamento Mensal', icon: CalendarIcon }
      ]
    },
    
    system: {
      label: 'Sistema',
      icon: UsersIcon,
      tabs: [
        { id: 'clinic-settings', label: 'Dados da Clínica', icon: Stethoscope },
        { id: 'users', label: 'Usuários', icon: UsersIcon },
        { id: 'patient-linking', label: 'Vincular Pacientes', icon: LinkIcon },
        { id: 'migrate-records', label: 'Migrar Prontuários', icon: FileText },
        { id: 'data-integrity', label: 'Integridade de Dados', icon: ShieldAlert },
        { id: 'data-quality', label: 'Qualidade de Dados', icon: ShieldAlert },
        { id: 'financial-migration-control', label: 'Ativação Financeira', icon: ShieldAlert }
      ]
    }
  };

  const visibleSections: Partial<typeof sections> = isFinanceUser
    ? { financial: sections.financial }
    : isInternalUser
    ? {
        dashboard: sections.dashboard,
        patients: {
          ...sections.patients,
          tabs: sections.patients.tabs.filter((tab) =>
            ['agenda', 'prontuario', 'documents', 'assessments-v2'].includes(tab.id)
          ),
        },
      }
    : {
        ...sections,
        patients: sections.patients,
        system: {
          ...sections.system,
          tabs: sections.system.tabs.filter(tab => isSuperAdmin || !restrictedSystemTabs.has(tab.id))
        }
      };
  const visibleSectionEntries = Object.entries(visibleSections).filter(
    (entry): entry is [string, NonNullable<typeof entry[1]>] => Boolean(entry[1])
  );
  const systemSection = visibleSections.system;
  const activeSectionTabs = visibleSectionEntries.find(([key]) => key === activeSection)?.[1].tabs ?? [];
  const displayedActiveSectionTabs = activeSection === 'financial'
    ? activeSectionTabs.filter((tab) => tab.id !== 'financial-overview')
    : activeSectionTabs;
  const defaultSystemTab = systemSection?.tabs[0]?.id ?? 'users';
  const visiblePatientTabs = visibleSections.patients?.tabs ?? [];
  const defaultPatientsTab = isFinanceUser
    ? 'financial-professionals'
    : isInternalUser
    ? visiblePatientTabs.find((tab) => tab.id === 'agenda')?.id
      ?? visiblePatientTabs[0]?.id
      ?? 'agenda'
    : visiblePatientTabs[0]?.id ?? 'patient-registration';

  const getAdminTabPath = (tabId: string) => buildTenantPath(`/admin/${tabId}`, tenantSlug);
  const getActiveAdminTabFromPath = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const adminIndex = pathSegments.lastIndexOf('admin');

    if (adminIndex === -1) {
      return '';
    }

    return pathSegments[adminIndex + 1] ?? '';
  };

  const handleTabChange = (sectionKey: string, tabId: string) => {
    if (!isSuperAdmin && restrictedSystemTabs.has(tabId)) {
      return;
    }
    setActiveSection(sectionKey as any);
    setActiveTab(tabId);
    navigate(getAdminTabPath(tabId), { replace: !tabId.startsWith('financial-') });
  };

  useEffect(() => {
    const path = getActiveAdminTabFromPath();

    if (!path && isFinanceUser) {
      setActiveSection('financial');
      setActiveTab('financial-professionals');
      navigate(getAdminTabPath('financial-professionals'), { replace: true });
      return;
    }
    if (!path && !isInternalUser) {
      setActiveSection('dashboard');
      setActiveTab('overview');
      return;
    }

    if (!path) {
      if (isInternalUser) {
        setActiveSection('patients');
        setActiveTab(defaultPatientsTab);
        navigate(getAdminTabPath(defaultPatientsTab), { replace: true });
      }
      return;
    }

    if (path === 'assessments-new') {
      setActiveSection('patients');
      setActiveTab('assessments-v2');
      navigate(getAdminTabPath('assessments-v2'), { replace: true });
      return;
    }

    if (path === 'financial-settlement-rules' || path === 'financial-absence-policies' || path === 'financial-cash-accounts') {
      setActiveSection('financial');
      setActiveTab(path);
      return;
    }

    if (path === 'financial-roles' || path === 'financial-expenses') {
      setActiveSection('financial');
      setActiveTab('financial-accounts-payable');
      return;
    }

    if (!isSuperAdmin && restrictedSystemTabs.has(path) && systemSection) {
      setActiveSection('system');
      setActiveTab(defaultSystemTab);
      navigate(getAdminTabPath(defaultSystemTab), { replace: true });
      return;
    }

    const foundSection = visibleSectionEntries.find(([_, section]) =>
      section.tabs.some(tab => tab.id === path)
    );

    if (foundSection) {
      const [sectionKey, section] = foundSection;
      const tab = section.tabs.find(t => t.id === path);
      if (tab) {
        setActiveSection(sectionKey as any);
        setActiveTab(tab.id);
      }
    } else if (isFinanceUser) {
      setActiveSection('financial');
      setActiveTab(defaultPatientsTab);
      navigate(getAdminTabPath(defaultPatientsTab), { replace: true });
    } else if (isInternalUser) {
      setActiveSection('patients');
      setActiveTab(defaultPatientsTab);
      navigate(getAdminTabPath(defaultPatientsTab), { replace: true });
    }
  }, [defaultPatientsTab, defaultSystemTab, isFinanceUser, isInternalUser, isSuperAdmin, location.pathname, location.search, navigate, systemSection, tenantSlug, visibleSections]);

  useEffect(() => {
    if (activeTab === 'assessments-new') {
      setActiveSection('patients');
      setActiveTab('assessments-v2');
      navigate(getAdminTabPath('assessments-v2'), { replace: true });
    }
  }, [activeTab, navigate, tenantSlug]);

  useEffect(() => {
    if (activeSection === 'patients' && !visiblePatientTabs.some(tab => tab.id === activeTab)) {
      setActiveTab(defaultPatientsTab);
      navigate(getAdminTabPath(defaultPatientsTab), { replace: true });
    }
  }, [activeSection, activeTab, defaultPatientsTab, navigate, tenantSlug, visiblePatientTabs]);
  const loadData = async () => {
    if (!user || !selectedClient) return;

    try {
      setIsLoading(true);
      const [fetchedRecords, fetchedUsers] = await Promise.all([
        getMedicalRecords(user.userId, selectedClient.userRole, selectedClient.clientId),
        UserService.getAllUsersByClientId(selectedClient.clientId)
      ]);
      setRecords(fetchedRecords as any);
      setUsers(fetchedUsers as any);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user, selectedClient]);

  useEffect(() => {
    if (activeTab !== 'documents' || !user || !selectedClient) return;

    let isCurrent = true;

    const refreshDocuments = async () => {
      try {
        const updatedRecords = await getMedicalRecords(
          user.userId,
          selectedClient.userRole,
          selectedClient.clientId
        );

        if (isCurrent) {
          setRecords(updatedRecords as MedicalRecord[]);
        }
      } catch (error) {
        console.error('Error refreshing documents:', error);
        if (isCurrent) {
          toast.error('Erro ao atualizar documentos');
        }
      }
    };

    void refreshDocuments();

    return () => {
      isCurrent = false;
    };
  }, [activeTab, user, selectedClient]);

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
  };

  const handleUserUpdate = async () => {
    try {
      if (selectedClient) {
        const updatedUsers = await UserService.getAllUsersByClientId(selectedClient.clientId);
        setUsers(updatedUsers as any);
      }
    } catch (error) {
      console.error('Error refreshing users:', error);
      toast.error('Erro ao atualizar lista de usuários');
    }
  };

  const handleDeleteUser = async (userToDelete: User) => {
    if (!selectedClient) return;

    try {
      await UserService.deleteUserFromClient(userToDelete.userId, selectedClient.clientId);
      toast.success(`Usuário ${userToDelete.userDisplayName} removido com sucesso.`);
      setShowDeleteUserConfirm(null);
      handleUserUpdate(); // Re-fetch users
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Erro ao remover usuário.');
    }
  };

  const filteredRecords = records.filter(record => {
    const normalizedSearch = normalizeSearchText(searchTerm);
    return (
      normalizeSearchText(record.name).includes(normalizedSearch) ||
      matchesPatientSearch(searchTerm, record.patientName) ||
      normalizeSearchText(record.doctorName).includes(normalizedSearch)
    );
  });

  const handleUploadComplete = async () => {
    setShowUploadModal(false);
    
    // Refresh records
    if (user && selectedClient) {
      const updatedRecords = await getMedicalRecords(user.userId, selectedClient.userRole, selectedClient.clientId);
      setRecords(updatedRecords as any);
    }
  };

  const handleDownload = async (record: MedicalRecord) => {
    try {
      const downloadUrl = await getMedicalRecordDownloadUrl(record.metadata);
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Erro ao gerar link de download do documento');
    }
  };

  const handleDelete = async (documentId: string) => {
    if (!selectedClient?.clientId) {
      toast.error('Tenant não selecionado');
      return;
    }

    try {
      await deleteMedicalRecord(documentId, selectedClient.clientId);
      setRecords(records.filter(record => record.id !== documentId));
      toast.success('Documento excluído com sucesso');
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Erro ao excluir documento');
    } finally {
      setShowDeleteConfirm(null);
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'doctor':
        return 'Médico';
      case 'patient':
        return 'Paciente';
      case 'interno':
        return 'Interno';
      default:
        return role;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-800';
      case 'doctor':
        return 'bg-blue-100 text-blue-800';
      case 'patient':
        return 'bg-green-100 text-green-800';
      case 'interno':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatAccessWindowLabel = (accessStartAt?: string | null, accessEndAt?: string | null) => {
    if (!accessStartAt && !accessEndAt) return null;

    const formatWindowDate = (value?: string | null) =>
      value ? new Date(value).toLocaleString('pt-BR') : 'aberto';

    return `${formatWindowDate(accessStartAt)} até ${formatWindowDate(accessEndAt)}`;
  };

  const formatDate = (dateValue: string | Date | Timestamp | null | undefined): string => {
    if (!dateValue) return 'Data não disponível';
    
    let date: Date;
    
    if (dateValue instanceof Timestamp) {
      date = dateValue.toDate();
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    } else {
      return 'Data inválida';
    }
    
    if (isNaN(date.getTime())) return 'Data inválida';
    
    return format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  };

  const renderTabContent = () => {
    if (!isSuperAdmin && restrictedSystemTabs.has(activeTab)) {
      return null;
    }

    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {/* Cards de Resumo */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-blue-500">
                <div className="flex items-center">
                  <UsersIcon className="w-8 h-8 text-blue-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Total de Usuários</p>
                    <p className="text-2xl font-semibold text-gray-900">{users.length}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-green-500">
                <div className="flex items-center">
                  <FileText className="w-8 h-8 text-green-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Documentos</p>
                    <p className="text-2xl font-semibold text-gray-900">{records.length}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-purple-500">
                <div className="flex items-center">
                  <GraduationCap className="w-8 h-8 text-purple-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Leads Ativos</p>
                    <p className="text-2xl font-semibold text-gray-900">-</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-orange-500">
                <div className="flex items-center">
                  <Calendar className="w-8 h-8 text-orange-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Eventos</p>
                    <p className="text-2xl font-semibold text-gray-900">-</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Ações Rápidas por Seção */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visibleSectionEntries.map(([sectionKey, section]) => {
                if (sectionKey === 'financial' && financialPreview && !['admin', 'financeiro'].includes(String(activeRole))) return null;
                if (sectionKey === 'dashboard') return null;
                const SectionIcon = section.icon;
                
                return (
                  <div key={sectionKey} className="bg-white rounded-lg shadow-sm p-6 border">
                    <div className="flex items-center mb-4">
                      <SectionIcon className="w-6 h-6 text-primary-600 mr-3" />
                      <h3 className="text-lg font-semibold text-gray-900">{section.label}</h3>
                    </div>
                    <div className="space-y-2">
                      {section.tabs.map(tab => {
                        const TabIcon = tab.icon;
                        return (
                          <button
                            key={tab.id}
                            onClick={() => handleTabChange(sectionKey, tab.id)}
                            className="w-full text-left p-2 rounded-md hover:bg-gray-50 transition-colors flex items-center"
                          >
                            <TabIcon className="w-4 h-4 text-gray-500 mr-2" />
                            <span className="text-sm text-gray-700">{tab.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        );
      case 'users':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-semibold text-gray-800">Usuários</h2>
              <button
                onClick={() => setShowCreateUser(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Novo Usuário
              </button>
            </div>

            {showCreateUser ? (
              <CreateUserPage 
                onClose={(shouldRefetch) => {
                  setShowCreateUser(false);
                  if (shouldRefetch) {
                    loadData(); // Recarrega os dados do painel
                  }
                }}
              />
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center">
                    <Search className="w-5 h-5 text-gray-400 mr-2" />
                    <input
                      type="text"
                      placeholder="Buscar usuários..."
                      className="flex-1 border-none focus:ring-0"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Nome
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Função
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Data de Cadastro
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.map((user) => (
                        <tr key={user.userId} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {user.userDisplayName}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{user.userEmail}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {selectedClient && (
                              <div className="space-y-1">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleColor(user.userRole ?? 'patient')}`}>
                                  {getRoleLabel(user.userRole ?? 'patient')}
                                </span>
                                {user.userRole === 'interno' && (
                                  <div className="text-xs text-gray-500">
                                    {formatAccessWindowLabel(user.accessStartAt, user.accessEndAt) || 'Sem período configurado'}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(user.userCreatedAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleEditUser(user)}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setShowDeleteUserConfirm(user)}
                              className="text-red-600 hover:text-red-900 ml-4"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      case 'clinic-settings':
        return <ClinicSettingsPage />;
      case 'home-page':
        return <HomePageEditor />;
      case 'about-page':
        return <AboutPageEditor />;
      case 'services-page':
        return <ServicesPageEditor />;
      case 'documents':
        return (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Buscar documentos..."
                    className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <ActionButton
                  onClick={() => setShowUploadModal(true)}
                  variant="primary"
                >
                  <PlusCircle className="h-4 w-4" />
                  Novo Documento
                </ActionButton>
              </div>
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Documento
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Paciente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Médico
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredRecords.map((record) => (
                      <tr key={record.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <FileText className="w-5 h-5 text-gray-400 mr-2" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">{record.name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{record.patientName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{record.doctorName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatDate(record.uploadDate)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => void handleDownload(record)}
                              className="text-primary-600 hover:text-primary-900"
                              title="Baixar"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            {activeRole !== 'interno' && (
                              <button
                                onClick={() => setShowDeleteConfirm(record.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Excluir"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredRecords.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-500">Nenhum documento encontrado</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      case 'leads':
        return <LeadsManager />;
      case 'courses':
        return <CoursesEditor />;
      case 'events':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-semibold text-gray-800">Eventos</h2>
              <button
                onClick={() => setShowEventEditor(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                Novo Evento
              </button>
            </div>

            {showEventEditor ? (
              <EventEditor onClose={() => setShowEventEditor(false)} />
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="p-6">
                  <p className="text-gray-600">Lista de eventos será implementada aqui.</p>
                </div>
              </div>
            )}
          </div>
        );
      case 'news':
        return <NewsEditor />;
      case 'testimonials':
        return <TestimonialsEditor />;
      case 'financial-new':
        return <NewFinancialDashboard />;
      case 'financial-overview':
      case 'financial-patients':
      case 'financial-invoices':
      case 'financial-professionals':
        return financialPreview ? <FinancialReadDashboard key={selectedClient?.clientId} /> : <NewFinancialDashboard />;
      case 'financial-cash-flow':
        return financialPreview ? <FinancialCashFlowPage key={selectedClient?.clientId} /> : <NewFinancialDashboard />;
      case 'financial-accounts-payable':
        return financialPreview ? <FinancialAccountsPayablePage key={selectedClient?.clientId} /> : <NewFinancialDashboard />;
      case 'financial-contracts':
      case 'financial-packages':
      case 'financial-organizations':
      case 'financial-payer-links':
      case 'financial-pricing-rules':
      case 'financial-settings':
      case 'financial-services':
      case 'financial-package-templates':
      case 'financial-absence-policies':
      case 'financial-cash-accounts':
        return <FinancialReadDashboard key={selectedClient?.clientId} />;
      case 'monthly-closing':
        return <MonthlyClosingPage />;
      case 'pricing-rules':
      case 'financial-settlement-rules':
        return <DoctorPricingRulesPage />;
      case 'agenda':
        return <AgendaPage />;
      case 'agenda-reports':
        return <AgendaReports />;
      case 'patient-registration':
        return <PatientRegistrationPage />;
      case 'prontuario':
        return <ProntuarioPage />;
      case 'assessments-new':
        return <NewAssessmentsV2Page />;
      case 'assessments-v2':
        return <NewAssessmentsV2Page />;
      case 'migrate-records':
        return <MigrateMedicalRecordsPage />;
      case 'patient-linking':
        return <PatientLinkingPage />;
      case 'data-integrity':
        return <DataIntegrityPage />;
      case 'data-quality':
        return <DataQualityDashboard />;
      case 'financial-migration-control':
        return <FinancialMigrationControlPage />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div
        className="bg-primary-600"
        style={{ minHeight: '112px', paddingBlock: '24px', display: 'flex', alignItems: 'center', overflow: 'visible' }}
      >
        <div className="container mx-auto px-4 w-full flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">
            {isInternalUser ? 'Painel Interno' : 'Painel Administrativo'}
          </h1>
          {user?.userDisplayName && (
            <div className="flex items-center gap-4">
              <span className="hidden text-sm text-slate-600 sm:inline">Olá, {user.userDisplayName}</span>
              {selectedClient && !isInternalUser && (
                <NotificationBell
                  clientId={selectedClient.clientId}
                  userId={user.userId}
                  professionalName={user.userDisplayName}
                  userRole={selectedClient.userRole}
                  onResolveAppointment={(appointment) => setSelectedAppointment(appointment)}
                />
              )}
            </div>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Navegação por Seções */}
        <div className="mb-8">
          {/* Seções Principais */}
          <div className="bg-white rounded-lg shadow-sm border mb-4">
            <div className="flex overflow-x-auto">
              {visibleSectionEntries.map(([sectionKey, section]) => {
                if (sectionKey === 'financial' && financialPreview && !['admin', 'financeiro'].includes(String(activeRole))) return null;
                const SectionIcon = section.icon;
                return (
                  <button
                    key={sectionKey}
                    onClick={() => {
                      const firstTab = section.tabs[0];
                      if (!firstTab) return;
                      handleTabChange(sectionKey, firstTab.id);
                    }}
                    className={`flex items-center px-6 py-4 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                      activeSection === sectionKey
                        ? 'border-primary-500 text-primary-600 bg-primary-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <SectionIcon className="w-4 h-4 mr-2" />
                    {section.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Abas da Seção Ativa */}
          {displayedActiveSectionTabs.length > 1 && (
            <div className="bg-gray-50 rounded-lg p-2">
              <div className="flex space-x-1 overflow-x-auto">
                {displayedActiveSectionTabs.map(tab => {
                  const TabIcon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(activeSection, tab.id)}
                      className={`flex items-center px-4 py-2 rounded-md font-medium text-sm whitespace-nowrap transition-colors ${
                        activeTab === tab.id || (['financial-settlement-rules', 'financial-absence-policies', 'financial-cash-accounts'].includes(activeTab) && tab.id === 'financial-settings')
                          ? 'bg-white text-primary-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                      }`}
                    >
                      <TabIcon className="w-4 h-4 mr-2" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {renderTabContent()}
      </div>

      {/* Modal de Upload */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-3 sm:p-5">
          <div className="max-h-[94vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-4 shadow-2xl sm:p-6">
            <h3 className="text-lg font-semibold mb-4">Adicionar Documento</h3>
            <DocumentUpload onUploadComplete={handleUploadComplete} />
            <button
              onClick={() => setShowUploadModal(false)}
              className="mt-4 text-sm text-neutral-600 hover:text-neutral-800"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Modal de Status de Agendamento */}
      {selectedAppointment && user && (
        <AppointmentStatusModal
          appointment={selectedAppointment}
          userId={user.userId}
          onClose={() => setSelectedAppointment(null)}
          onResolved={() => {
            setSelectedAppointment(null);
            loadData();
          }}
        />
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirmar Exclusão</h3>
            <p className="text-neutral-600 mb-6">
              Tem certeza que deseja excluir este documento? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end space-x-3">
              <ActionButton
                onClick={() => setShowDeleteConfirm(null)}
                variant="neutral"
              >
                Cancelar
              </ActionButton>
              <ActionButton
                onClick={() => handleDelete(showDeleteConfirm)}
                variant="danger"
              >
                Excluir
              </ActionButton>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {selectedUser && (
        <EditUserModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onUpdate={handleUserUpdate}
        />
      )}

      {/* Modal de Confirmação de Exclusão de Usuário */}
      {showDeleteUserConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirmar Exclusão</h3>
            <p className="text-neutral-600 mb-6">
              Tem certeza que deseja remover o usuário{" "}
              <strong>{showDeleteUserConfirm.userDisplayName}</strong> do
              tenant atual? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end space-x-3">
              <ActionButton
                onClick={() => setShowDeleteUserConfirm(null)}
                variant="neutral"
              >
                Cancelar
              </ActionButton>
              <ActionButton
                onClick={() => handleDeleteUser(showDeleteUserConfirm)}
                variant="danger"
              >
                Excluir
              </ActionButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
